import moment from 'moment';

import pinImage from '../assets/markers/pins/pin-empty.png';
import avatarImage from '../assets/markers/avatars/avatar-default.svg';
import DeviceMessage from './DeviceMessage';

import { BlePin, CriticalBatteryPin, DefaultPin, GpsPin, LockPin, SmcPin } from './Pins';
import GeometryService from '../services/GeometryService';

export default class Device {
	zoomLevel = 17;
	pinImage = pinImage;
	avatarImage = avatarImage;
	stateIndex = null;
	messages = [];
	message = null;

	constructor(deviceData) {
		Object.assign(this, deviceData);
		this.updateMessages();
	}

	isShowingHistory() {
		return !!this.stateIndex;
	}

	getNextState() {
		if (!this.stateIndex) return null;
		return this.states[this.stateIndex - 1];
	}

	getState() {
		if(!this.latest_state) return null;

		return this.stateIndex === null
			? this.latest_state
			: this.states[this.stateIndex];
	}

	stateWasActiveForMinutes() {
		const state = this.getState();
		if(!state) return "";

		const nextState = this.getNextState();
		const currentStateMoment = moment.utc(state.send_at)
			.local();
		const nextStateMoment = nextState ? moment.utc(nextState.send_at)
			.local() : moment.utc()
			.local();

		return currentStateMoment.from(nextStateMoment, true);
	}

	iconIsLetter() {
		return this.icon_slug.length === 1;
	}

	getIcon() {
		if (this.iconIsLetter()) {
			return this.icon_slug;
		}

		return String.fromCharCode(parseInt(this.icon_slug, 16));
	}

	showCircle() {
		return this.getAccuracy() >= 50;
	}

	getFirmware() {
		const state = this.getState();
		if(!state) return "-";

		return state.firmware
			? [state.firmware.slice(0, 1), '.', state.firmware.slice(1)].join('')
			: '';
	}

	getMarkerIcon() {
		// this.a = '';
		return {
			fontFamily: 'Fontawesome',
			fontSize: '22px',
			text: this.getIcon(),
			color: '#17263C',
		};
	}

	getImages() {
		return this.images;
	}

	// Old way of drawing markers
	getMarker() {
		if (this.security_service_mode) return SmcPin;
		if (this.latest_state && this.latest_state.battery_percentage <= 10) return CriticalBatteryPin;
		if (this.isDetectingRange()) return BlePin;
		if (this.isArmed()) return GpsPin;
		if (this.location_lock) return LockPin;
		return DefaultPin;
	}

	getZoomLevel() {
		const state = this.getState();
		if(!state) return 16;

		if (state.accuracy >= 10000) {
			return 10;
		} else if (state.accuracy >= 3000) {
			return 12;
		} else if (state.accuracy >= 1000) {
			return 12;
		} else if (state.accuracy >= 500) {
			return 13;
		} else if (state.accuracy >= 200) {
			return 14;
		} else if (state.accuracy >= 50) {
			return 15;
		}
		return 16;
	}

	getAccuracy() {
		const state = this.getState();
		if(!state) return 0;

		return Number(state.accuracy) || 0;
	}

	getLocation() {
		const state = this.getState();
		if(!state) return {
			lat: parseFloat(0),
			lng: parseFloat(0),
		};

		return {
			lat: parseFloat(state.location.latitude),
			lng: parseFloat(state.location.longitude),
		};
	}

	getDistance(location) {
		if(!location) return 0;
		return Math.floor(GeometryService.distance(this.getLocation(), location));
	}

	getLocationWithCircleOffset() {
		if (!this.showCircle()) return this.getLocation();

		return GeometryService.locationOffset(this.getLocation(), 45, this.getAccuracy());
	}


	getLastUpdate() {
		const state = this.getState();
		if(!state) return moment.now();

		return moment.utc(state.send_at)
			.local();
	}

	getLastFormattedUpdate(format) {
		return this.getLastUpdate()
			.format(format);
	}

	getSubtitle() {
		if (!this.is_registered) {
			return 'Abonnement verlopen';
		}

		if (this.isWaitingForActivation()) {
			return 'Koppel deze E-finder';
		}

		const state = this.getState();
		if(!state) return 'Wacht op eerste bericht';

		return 'Laatste update ' + moment.utc(state.send_at)
			.local()
			.fromNow();
	}

	getStateMessageType() {
		const state = this.getState();
		if(!state) return '-';

		return state.type;
	}

	hasStreetView() {
		// TODO: Check @ google if available
		const state = this.getState();
		if(!state) return false;

		return state.accuracy < 50;
	}

	getMessage(index) {
		if (this.messages.length === 0) return '';
		return this.messages[index % this.messages.length].message;
	}

	getMessageIcon(index) {
		if (this.messages.length === 0) return '';

		// Show previous icon while armed_mode should be showed
		if(this.messages[index % this.messages.length].slug === 'armed-state'){
			const previous = this.messages[(index - 1) % this.messages.length] ;
			const previousIcon = previous ? previous.slug : '';
			return previousIcon !== 'armed-state' ? previousIcon : '';
		}

		return this.messages[index % this.messages.length].slug;
	}

	isArmed() {
		const state = this.getState();
		if(!state) return false;

		return (state.state_id == 1 || state.state_id == 2)
			&& (this.target_mode == 1 || this.target_mode == 2);
	}

	isDetectingRange() {
		const state = this.getState();
		if(!state) return false;

		return state.state_id == 3 && this.target_mode == 3;
	}

	isMoving() {
		const state = this.getState();
		if(!state) return false;

		return state.is_moving;
	}

	isWaitingForActivation() {
		return this.is_registered && !this.activated_at;
	}

	updateMessages() {
		this.messages = [];

		if (this.isWaitingForActivation()) {
			this.messages = [new DeviceMessage('activation', 'Activeer je E-finder')];
			return;
		}

		if (this.security_service_mode) {
			this.messages = [new DeviceMessage('security-service-mode', 'SMC alarm service actief')];
			return;
		}

		if (this.isDetectingRange()) {
			this.messages.push(new DeviceMessage('range-detector', 'Pinpointer modus actief - batterijduur korter'));
			return;
		}

		if (this.isArmed()) {
			this.messages.push(new DeviceMessage('armed-state', 'Zoeken profiel actief - batterijduur korter'));
		}

		if (this.location_lock) {
			this.messages.push(new DeviceMessage('location-lock', 'Beweging meldingen ingeschakeld'));
		}

		if (this.latest_state && this.latest_state.battery_percentage <= 10) {
			this.messages.push(new DeviceMessage('battery-critical', 'Batterijniveau kritiek'));
		} else if (this.latest_state && this.latest_state.battery_percentage <= 25) {
			this.messages.push(new DeviceMessage('battery-low', 'Batterijniveau laag'));
		}
	}
}
